import { css, cx } from "@emotion/css";
import { ExclamationCircleIcon, ExclamationTriangleIcon, GemIcon, RocketIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, borderWidth, lineHeight, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
import type { DesignSystemLinkHref } from "../../routing";
import { useOctopusLinkComponent } from "../../routing";
import { ExternalLink } from "../Link/ExternalLink";
export const inlineMessageTypes = ["information", "warning", "danger", "feature", "upsell"] as const;
export type InlineMessageType = (typeof inlineMessageTypes)[number];
interface ActionButtonProps {
    /** The text label displayed on the action button */
    label: string;
    /** Optional click handler for the action button */
    onClick?: () => void;
}
interface ActionLinkPropsBase {
    /** The text label displayed on the action button */
    label: string;
    /** The href of the action link */
    href: DesignSystemLinkHref;
}
interface ActionLinkPropsExternal extends ActionLinkPropsBase {
    /** Optional prop to determine if the link is external */
    external: true;
    /** Optional prop to track analytics for the action link if its an external link */
    trackAnalytics?: boolean;
}
type ActionLinkProps = ActionLinkPropsBase | ActionLinkPropsExternal;
export interface InlineMessageProps {
    /** Available types for inline messages used for styling and icons*/
    type: InlineMessageType;
    /** Optional bolded title text */
    title?: string;
    /** The main message content */
    description: string;
    /** Optional action button */
    action?: ActionButtonProps | ActionLinkProps;
}
/**
 * The inline message component is used to display a short, non-urgent contextual message that
 * attracts the user's attention without interrupting their task. It can be used to display information,
 * warnings, or other messages that are not critical to the user's task.
 *
 * @param props - The component props
 * @returns A React element representing the inline message
 *
 * @example
 * ```tsx
 * // Basic information message
 * <InlineMessage
 *   type="information"
 *   description="Your changes have been saved successfully."
 * />
 *
 * // With action button
 * <InlineMessage
 *   type="warning"
 *   title="Deprecated Feature"
 *   description="This feature will be removed in the next version."
 *   action={{ label: "Learn More", onClick: onClick }}
 * />
 * ```
 */
/**
 * Type guard to check if action is ActionButtonProps
 */
function isActionButton(action: ActionButtonProps | ActionLinkProps): action is ActionButtonProps {
    return "onClick" in action;
}
/**
 * Type guard to check if action is ActionLinkProps
 */
function isActionLink(action: ActionButtonProps | ActionLinkProps): action is ActionLinkProps {
    return "href" in action;
}
/**
 * Type guard to check if action is ActionLinkPropsExternal
 */
function isActionLinkExternal(action: ActionLinkProps): action is ActionLinkPropsExternal {
    return "external" in action && action.external === true;
}
function ActionElement(action: ActionButtonProps | ActionLinkProps) {
    const Link = useOctopusLinkComponent();
    if (action && isActionLink(action)) {
        if (isActionLinkExternal(action) && typeof action.href === "string") {
            return (<div className={actionExternalStyles}>
                    <ExternalLink href={action.href} trackAnalytics={action.trackAnalytics}>
                        {action.label}
                    </ExternalLink>
                </div>);
        }
        return (<Link className={cx(actionStyles, interactionStyles)} target="_self" href={action.href}>
                {action.label}
            </Link>);
    }
    if (action && isActionButton(action)) {
        return (<button type="button" className={cx(buttonStyles, actionStyles, interactionStyles)} onClick={action.onClick}>
                {action.label}
            </button>);
    }
}
export const InlineMessage = (props: InlineMessageProps) => {
    const { type, title, description, action } = props;
    const iconElement = getIconForType(type);
    return (<div className={containerStyles}>
            <span className={iconContainerStyles}>{iconElement}</span>
            <div className={contentContainerStyles}>
                {title && <p className={titleStyles}>{title}</p>}
                <p className={messageStyles}>{description}</p>
                {action && ActionElement(action)}
            </div>
        </div>);
};
InlineMessage.displayName = "InlineMessage";
const iconsByType: Record<InlineMessageType, React.ReactElement> = {
    information: <ExclamationCircleIcon size={20} color={themeTokens.color.icon.info}/>,
    warning: <ExclamationCircleIcon size={20} color={themeTokens.color.icon.warning}/>,
    danger: <ExclamationTriangleIcon size={20} color={themeTokens.color.icon.danger}/>,
    feature: <RocketIcon size={20} color={themeTokens.color.icon.feature}/>,
    upsell: <GemIcon size={20} color={themeTokens.color.icon.feature}/>,
};
function getIconForType(type: InlineMessageType): React.ReactNode {
    return iconsByType[type] || null;
}
const containerStyles = css({
    display: "flex",
    gap: space[4],
    // Fixing the line height here to prevent alignment issues when a parent container has a line height set
    lineHeight: lineHeight.xSmall,
});
const iconContainerStyles = css({
    flexShrink: 0,
});
const contentContainerStyles = css({
    display: "inline",
    whiteSpace: "normal",
    textWrap: "pretty",
});
const titleStyles = css({
    font: text.body.bold.medium,
    color: themeTokens.color.text.primary,
    display: "inline",
    margin: 0,
    marginRight: space[4],
});
const messageStyles = css({
    font: text.body.regular.medium,
    color: themeTokens.color.text.primary,
    display: "inline",
    margin: 0,
});
const actionStyles = css({
    font: text.body.regular.medium,
    color: themeTokens.color.text.link.default,
    marginLeft: space[4],
    textDecoration: "none",
});
const actionExternalStyles = css({
    display: "inline-flex",
    alignItems: "center",
    gap: space[2],
    marginLeft: space[4],
    // TODO: Ideally i want to apply this to the Link/ExternalLink component but that has
    // wider flow on effects, so this is temporary until we consolidate the Linking components
    // at a later date.
    "&:hover a": {
        color: themeTokens.color.text.link.hover,
        textDecoration: "underline",
    },
    "&:focus-visible a": {
        borderRadius: borderRadius.extraSmall,
        outline: `${borderWidth[2]} solid ${themeTokens.color.border.selected}`,
        outlineOffset: "2px",
    },
});
const buttonStyles = css({
    background: "none",
    padding: "0",
    border: "none",
    cursor: "pointer",
});
const interactionStyles = css({
    "&:hover": {
        color: themeTokens.color.text.link.hover,
        textDecoration: "underline",
        textDecorationColor: themeTokens.color.text.link.hover,
    },
    "&:focus-visible": {
        borderRadius: borderRadius.extraSmall,
        outline: `${borderWidth[2]} solid ${themeTokens.color.border.selected}`,
        outlineOffset: "2px",
    },
});
